import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { RecipesTranslationHeaderComponent } from '@recipes/recipes-translation/recipes-translation-header/recipes-translation-header.component';
import { RecipesTranslationIngredientsComponent } from '@recipes/recipes-translation/recipes-translation-ingredients/recipes-translation-ingredients.component';
import { RecipesTranslationStepsComponent } from '@recipes/recipes-translation/recipes-translation-steps/recipes-translation-steps.component';
import { RecipesTranslationTipsComponent } from '@recipes/recipes-translation/recipes-translation-tips/recipes-translation-tips.component';
import { RecipesTranslationTitlesSectionComponent } from '@recipes/recipes-translation/recipes-translation-titles-section/recipes-translation-titles-section.component';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';
import { MissingInputError } from '@shared/errors/missing-input/missing-input.error';
import {
  mapRecipeLiteToVariantTranslations,
  mapVariantTranslationToEditRecipeLite,
} from '@shared/mappers/recipe-translation/recipe-translation.mapper';
import { LocalizedKeyValue } from '@shared/models/dataref.model';
import { EditRecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { RecipeTranslationForm, RecipeTranslationVariant, TranslatedVariants } from '@shared/models/recipe/recipes-translation-form.model';
import { filter } from 'rxjs/operators';

type VariantData = Pick<EditRecipeLite, 'domain' | 'market' | 'lang'>;

@Component({
  selector: 'app-recipes-translation-form',
  templateUrl: './recipes-translation-form.component.html',
  styleUrls: ['./recipes-translation-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    RecipesTranslationTitlesSectionComponent,
    RecipesTranslationIngredientsComponent,
    RecipesTranslationTipsComponent,
    TranslateModule,
    RecipesTranslationHeaderComponent,
    RecipesTranslationStepsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecipesTranslationFormComponent implements OnInit {
  @Input() translatedVariantsData: {
    translatedVariants: TranslatedVariants;
    stepTypes: Record<string, LocalizedKeyValue>;
  };

  @Output() translationUpdated = new EventEmitter<{
    recipes: EditRecipeLite[];
    isApproved: boolean;
  }>();

  translationForm: FormGroup<RecipeTranslationForm>;

  variants: {
    source: RecipeTranslationVariant[];
    target: RecipeTranslationVariant[];
  };

  sourceData: VariantData;
  targetData: VariantData;

  constructor(private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    if (!this.translatedVariantsData) {
      throw new MissingInputError('translatedVariantsData');
    }

    this.variants = this.buildVariants(this.translatedVariantsData.translatedVariants);
    this.translationForm = this.buildRecipeTranslationForm(this.variants);
    this.sourceData = this.getVariantData(this.translatedVariantsData.translatedVariants.sourceRecipes[0]);
    this.targetData = this.getVariantData(this.translatedVariantsData.translatedVariants.targetRecipes[0]);
  }

  onTranslationApproved(isApproved: boolean): void {
    if (!this.translationForm.valid) {
      this.translationForm.markAllAsTouched();
      return;
    }

    if (isApproved) {
      this.openDialogAndSaveRecipe();
    } else {
      this.translationUpdated.emit({
        recipes: this.updateRecipes(isApproved),
        isApproved,
      });
    }
  }

  isFormValid(): boolean {
    return this.variants.target.every((v) => v.form.valid) && this.translationForm.valid;
  }

  private getVariantData(variant: EditRecipeLite): VariantData {
    return {
      domain: variant.domain,
      market: variant.market,
      lang: variant.lang,
    };
  }

  private buildVariants(translatedVariants: TranslatedVariants): {
    source: RecipeTranslationVariant[];
    target: RecipeTranslationVariant[];
  } {
    const sourceIngredients = translatedVariants.sourceRecipes.map((sourceRecipe) => sourceRecipe.ingredients);
    const COOKEO_DOMAIN = 'PRO_COO';
    const canHaveShortDescription =
      translatedVariants.sourceRecipes[0].domain === COOKEO_DOMAIN && translatedVariants.targetRecipes[0].domain === COOKEO_DOMAIN;

    return {
      source: mapRecipeLiteToVariantTranslations(translatedVariants.sourceRecipes, canHaveShortDescription, {
        disabled: true,
      }),
      target: mapRecipeLiteToVariantTranslations(translatedVariants.targetRecipes, canHaveShortDescription, {
        sourceIngredients,
      }),
    };
  }

  private buildRecipeTranslationForm(variants: {
    source: RecipeTranslationVariant[];
    target: RecipeTranslationVariant[];
  }): FormGroup<RecipeTranslationForm> {
    return new FormGroup({
      source: new FormGroup({
        titles: variants.source[0].form.controls.titles,
        tips: variants.source[0].form.controls.tips,
      }),
      target: new FormGroup({
        titles: variants.target[0].form.controls.titles,
        tips: variants.target[0].form.controls.tips,
      }),
    });
  }

  private openDialogAndSaveRecipe(): void {
    this.dialog
      .open(TextPopinComponent, {
        width: 'auto',
        data: {
          title: 'RECIPE_TRANSLATION.DIALOG.TITLE',
          text: 'RECIPE_TRANSLATION.DIALOG.MSG',
          validateBtn: 'RECIPE_TRANSLATION.DIALOG.BTN_YES',
          cancelBtn: 'RECIPE_TRANSLATION.DIALOG.BTN_NO',
        },
      })
      .afterClosed()
      .pipe(filter((confirmed) => !!confirmed))
      .subscribe(() => {
        this.translationUpdated.emit({
          recipes: this.updateRecipes(true),
          isApproved: true,
        });
      });
  }

  private updateRecipes(isReady: boolean): EditRecipeLite[] {
    return this.variants.target.map((variant) => {
      const original = this.translatedVariantsData.translatedVariants.targetRecipes.find(
        (recipe) => recipe.identifier.functionalId === variant.functionalId,
      );
      if (!original) {
        throw new Error(`Original recipe not found for functionalId: ${variant.functionalId}`);
      }

      return mapVariantTranslationToEditRecipeLite(original, variant, isReady);
    });
  }
}
